import { toast } from '@buffer-mono/popcorn'
import { useCallback } from 'react'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'
import {
  DismissAllCommentsByChannelIdMutation,
  type DismissAllCommentsByChannelId,
} from './useDismissAll'

export enum DismissAllCommentsStatus {
  Processed = 'processed',
  Enqueued = 'enqueued',
}

export type UseDismissAllCommentByChannelIdResult = {
  dismissAllCommentsByChannelId: DismissAllCommentsByChannelId
  loading: boolean
}

/**
 * Hook to dismiss all comments in a channel
 *
 * @returns {UseDismissAllCommentByChannelIdResult} - The dismissAllCommentsByChannelId function
 *
 * @example
 *
 * const { dismissAllCommentsByChannelId } = useDismissAllCommentsByChannelId()
 *
 * const handleDismissAllCommentsByChannelId = async () => {
 *  await dismissAllCommentsByChannelId(channelId)
 * }
 *
 */
export const useDismissAllCommentsByChannelId =
  (): DismissAllCommentsByChannelId => {
    const [dismissAllMutation] = useTypedMutation(
      DismissAllCommentsByChannelIdMutation,
      (data) => data.dismissAllCommentsByChannelId,
      {
        successTypename: 'DismissAllCommentsSuccess',
        refetchQueries: ['Comments'],
      },
    )

    const dismissAllCommentsByChannelId = useCallback(
      async (channelId: string): Promise<void> => {
        if (!channelId) {
          throw new Error('Channel id is required')
        }

        try {
          const { data } = await dismissAllMutation({
            variables: { input: { channelId } },
          })

          if (data && data?.status === DismissAllCommentsStatus.Processed) {
            const dismissedCount = data?.affectedCommentCount || 0
            if (dismissedCount === 0) {
              toast.success("We couldn't find any comment to resolve")
            } else {
              toast.success(
                `Successfully resolved ${dismissedCount} comment${
                  dismissedCount === 1 ? '' : 's'
                }`,
              )
              // Send event to Clarity to easily track errors in the community
              if (window.clarity) {
                window.clarity(
                  'set',
                  'community_comment_dismiss_all_success',
                  String(dismissedCount),
                )
              }
            }
          } else {
            toast.error('An error occurred while resolving the comments')

            if (window.clarity) {
              window.clarity(
                'set',
                'community_comment_dismiss_all_error',
                channelId,
              )
            }
          }
        } catch {
          toast.error('An error occurred while resolving the comments')

          if (window.clarity) {
            window.clarity(
              'set',
              'community_comment_dismiss_all_error',
              channelId,
            )
          }
        }
      },
      [dismissAllMutation],
    )

    return dismissAllCommentsByChannelId
  }
